import { useState } from "react";

import { useDeleteRun } from "../../hooks/useProjects";

interface Props {
  runId: string;
  runNumber: number;
  studyId: string;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * "Delete forever" confirmation modal for a single run.
 *
 * Mirrors :class:`DeleteProjectModal` pixel-for-pixel — same backdrop,
 * card, type-to-confirm input, danger button. Runs don't have a
 * user-facing name, so the typed-confirmation token is the run id
 * (``run-XXXXXX``); that matches the identifier visible in the URL
 * and the row's data-testid.
 *
 * The modal calls ``DELETE /api/runs/:id`` via :func:`useDeleteRun`,
 * which invalidates the parent study's runs query on success.
 */
export function DeleteRunModal({
  runId,
  runNumber,
  studyId,
  onClose,
  onDeleted,
}: Props) {
  const [typed, setTyped] = useState("");
  const mutation = useDeleteRun(studyId);
  const matches = typed.trim() === runId;

  const onConfirm = async () => {
    if (!matches) return;
    try {
      await mutation.mutateAsync(runId);
      onDeleted();
    } catch {
      // mutation.isError surfaces below
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-run-title"
      data-testid="delete-run-modal"
      style={styles.backdrop}
    >
      <div style={styles.card}>
        <h2 id="delete-run-title" style={styles.title}>
          Delete this run?
        </h2>
        <p style={styles.body}>
          <strong>Run #{runNumber}</strong>'s layout output will be permanently
          removed. This cannot be undone.
        </p>

        <label style={styles.label} htmlFor="delete-run-confirm-input">
          Type the run identifier to confirm: <code>{runId}</code>
        </label>
        <input
          id="delete-run-confirm-input"
          data-testid="delete-run-confirm-input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          style={styles.input}
          autoComplete="off"
          spellCheck={false}
        />

        {mutation.isError && (
          <div role="alert" data-testid="delete-run-error" style={styles.error}>
            Couldn't delete the run. Please retry, or contact support if it
            keeps failing.
          </div>
        )}

        <div style={styles.actions}>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            data-testid="delete-run-cancel"
            style={styles.cancelLink}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={!matches || mutation.isPending}
            data-testid="delete-run-confirm-button"
            style={
              matches && !mutation.isPending
                ? styles.danger
                : styles.dangerDisabled
            }
          >
            {mutation.isPending ? "Deleting…" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

const DANGER = "#dc2626";
const DANGER_HOVER = "#b91c1c";

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: "1rem",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "#ffffff",
    borderRadius: 12,
    padding: "1.75rem",
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.04), 0 18px 36px -8px rgba(15, 23, 42, 0.18)",
  },
  title: {
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    margin: "0 0 0.75rem",
    color: "#0a0e27",
  },
  body: {
    fontSize: "0.9375rem",
    color: "#475569",
    lineHeight: 1.55,
    margin: "0 0 1rem",
  },
  label: {
    display: "block",
    fontSize: "0.8125rem",
    color: "#475569",
    margin: "0 0 0.375rem",
  },
  input: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontFamily: "inherit",
    marginBottom: "1rem",
  },
  error: {
    background: "#fff7f5",
    color: "#9a3412",
    border: "1px solid #fed7aa",
    borderRadius: 6,
    padding: "0.625rem 0.75rem",
    fontSize: "0.8125rem",
    marginBottom: "0.75rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "1rem",
  },
  cancelLink: {
    background: "transparent",
    color: "#475569",
    border: "none",
    padding: "0.5rem 0.5rem",
    fontSize: "0.9375rem",
    cursor: "pointer",
  },
  danger: {
    background: DANGER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  dangerDisabled: {
    background: DANGER_HOVER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
